import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";


const BUILDS_OF = [
    {
        title: "Marketing & booking websites",
        desc: "Fast, polished sites that tell your story and turn visitors into booked appointments — like this one.",
    },
    {
        title: "Web apps & customer portals",
        desc: "Dashboards, booking tools, client portals, and internal systems built around how your business actually runs.",
    },
    {
        title: "E-commerce & payments",
        desc: "Online stores wired into Square or Stripe, with product management and sales reporting built in.",
    },
];

const PACKAGES = [
    {
        name: "Starter Site",
        price: "$995",
        timeline: "2–3 weeks",
        testid: "starter",
        features: [
            "3–5 page custom website",
            "Mobile-first, fast, and searchable on Google",
            "Contact form + basic booking link",
            "Launch, hosting setup & handoff training",
        ],
    },
    {
        name: "Business Platform",
        price: null,
        timeline: "4–6 weeks",
        testid: "platform",
        featured: true,
        features: [
            "Everything in Starter Site",
            "Online booking & scheduling built in",
            "Automated email confirmations & reminders",
            "Customer database & simple CRM",
            "Payment collection (Stripe / Square)",
        ],
    },
    {
        name: "Custom Web App",
        price: null,
        timeline: "6–8 weeks",
        testid: "webapp",
        features: [
            "Everything in Business Platform",
            "Custom portal, dashboard, or internal tool",
            "Workflow automation & integrations",
            "E-commerce storefront if needed",
            "Priority support window after launch",
        ],
    },
];

const PROCESS = [
    { step: "01", title: "Free discovery call", desc: "One hour — in person or on Google Meet. We map what you need and what it should do." },
    { step: "02", title: "Written proposal", desc: "Within 48 hours you get an exact price and timeline. No hourly surprises." },
    { step: "03", title: "Build with weekly check-ins", desc: "You see real progress every week and can course-correct early." },
    { step: "04", title: "Launch & 30-day support", desc: "Training, plain-English docs, and a month of fixes and tweaks included." },
];

const INCLUDED = [
    "You own the code and the domain — no lock-in",
    "Security and backups set up from day one",
    "Plain-English communication, no tech jargon",
    "Training so you can run it yourself",
];

export default function WebDev() {
    return (
        <div className="bg-paper text-ink min-h-screen" data-testid="webdev-page">
            <header className="border-b border-ink/10">
                <div className="mx-auto max-w-7xl px-5 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="webdev-back-link" className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-worx transition-colors">
                        <ArrowLeft size={16} /> Back to site
                    </Link>
                    <span className="font-display font-semibold tracking-tight">Consulting WorX</span>
                    <a
                        href="/?book=web-dev#booking"
                        data-testid="webdev-book-cta"
                        className="inline-flex items-center gap-1.5 bg-ink text-paper px-4 py-2 text-sm hover:bg-worx transition-colors"
                    >
                        Book a free consult <ArrowUpRight size={14} />
                    </a>
                </div>
            </header>

            <main>
                <section className="pt-16 md:pt-24 pb-16 md:pb-24">
                    <div className="mx-auto max-w-7xl px-5 md:px-10">
                        <Reveal>
                            <p className="flex items-center gap-3 text-xs md:text-sm tracking-[0.25em] uppercase text-ink/50">
                                <span className="h-px w-10 bg-worx inline-block" /> Services — 01
                            </p>
                            <h1 className="mt-6 font-display font-semibold tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-4xl">
                                Website & web app <span className="text-worx">development.</span>
                            </h1>
                            <p className="mt-6 text-ink/60 max-w-xl text-base md:text-lg leading-relaxed">
                                A real website or custom web application for your business — designed, built,
                                and launched for you. Every project starts with a free discovery call and a
                                written proposal, so you know the price before any work begins.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <a
                                    href="/?book=web-dev#booking"
                                    data-testid="webdev-hero-book"
                                    className="inline-flex items-center gap-2 bg-worx text-white px-7 py-3.5 font-medium hover:bg-ink transition-colors duration-200"
                                >
                                    Book a free dev consult <ArrowUpRight size={16} />
                                </a>
                                <a
                                    href="/#portfolio"
                                    data-testid="webdev-hero-builds"
                                    className="inline-flex items-center gap-2 border border-ink px-7 py-3.5 font-medium hover:bg-ink hover:text-paper transition-colors duration-200"
                                >
                                    See recent builds
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </section>

                <section className="bg-ink text-paper py-20 md:py-28" data-testid="webdev-what">
                    <div className="mx-auto max-w-7xl px-5 md:px-10">
                        <Reveal>
                            <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl">What I build</h2>
                        </Reveal>
                        <div className="mt-10 grid md:grid-cols-3 gap-px bg-paper/15 border border-paper/15">
                            {BUILDS_OF.map((b, i) => (
                                <Reveal key={b.title} delay={i * 0.08}>
                                    <div className="bg-ink p-8 md:p-10 h-full hover:bg-[#141414] transition-colors duration-300">
                                        <span className="font-display text-worx text-lg">0{i + 1}</span>
                                        <h3 className="mt-3 font-display font-medium tracking-tight text-2xl">{b.title}</h3>
                                        <p className="mt-3 text-paper/60 leading-relaxed">{b.desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="py-20 md:py-28" data-testid="webdev-packages">
                    <div className="mx-auto max-w-7xl px-5 md:px-10">
                        <Reveal>
                            <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl">Packages</h2>
                            <p className="mt-4 text-ink/60 max-w-xl leading-relaxed">
                                Starter Sites have a fixed starting price. Business Platforms and Custom Web Apps
                                are scoped to your pages, integrations, and workflow — every project begins with a
                                free discovery call, and you get a written proposal before any work begins.
                            </p>
                        </Reveal>
                        <div className="mt-10 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                            {PACKAGES.map((p, i) => (
                                <Reveal key={p.name} delay={i * 0.08}>
                                    <div
                                        className={`p-8 md:p-10 h-full flex flex-col ${p.featured ? "bg-ink text-paper" : "bg-white"}`}
                                        data-testid={`package-${p.testid}`}
                                    >
                                        {p.featured && (
                                            <span className="text-xs uppercase tracking-[0.2em] text-worx font-medium mb-3">Most popular</span>
                                        )}
                                        <h3 className="font-display font-medium tracking-tight text-2xl">{p.name}</h3>
                                        {p.price ? (
                                            <div className="mt-4 flex items-baseline gap-2">
                                                <span className={`text-sm ${p.featured ? "text-paper/50" : "text-ink/50"}`}>from</span>
                                                <span className="font-display font-semibold tracking-tighter text-5xl">{p.price}</span>
                                            </div>
                                        ) : (
                                            <p className="mt-4 font-display font-semibold tracking-tighter text-4xl text-worx">Custom proposal</p>
                                        )}
                                        <p className={`mt-1 text-sm ${p.featured ? "text-paper/50" : "text-ink/50"}`}>Typical timeline: {p.timeline}</p>
                                        <ul className="mt-6 space-y-2.5 flex-1">
                                            {p.features.map((f) => (
                                                <li key={f} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-paper/80" : "text-ink/80"}`}>
                                                    <Check size={15} className="text-worx mt-0.5 shrink-0" />
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <a
                                            href="/?book=web-dev#booking"
                                            data-testid={`package-${p.testid}-cta`}
                                            className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-medium transition-colors duration-200 ${
                                                p.featured
                                                    ? "bg-worx text-white hover:bg-paper hover:text-ink"
                                                    : "border border-ink hover:bg-ink hover:text-paper"
                                            }`}
                                        >
                                            Start with a free call <ArrowUpRight size={14} />
                                        </a>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="border-y border-ink/10 py-20 md:py-28" data-testid="webdev-process">
                    <div className="mx-auto max-w-7xl px-5 md:px-10">
                        <Reveal>
                            <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl">How it works</h2>
                        </Reveal>
                        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                            {PROCESS.map((s, i) => (
                                <Reveal key={s.step} delay={i * 0.08}>
                                    <div className="bg-white p-8 h-full">
                                        <span className="font-display text-worx text-lg">{s.step}</span>
                                        <h3 className="mt-3 font-display font-medium tracking-tight text-xl">{s.title}</h3>
                                        <p className="mt-2 text-sm text-ink/60 leading-relaxed">{s.desc}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.15}>
                            <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-3xl">
                                {INCLUDED.map((x) => (
                                    <li key={x} className="flex items-start gap-2.5 text-ink/80">
                                        <Check size={16} className="text-worx mt-0.5 shrink-0" />
                                        <span>{x}</span>
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </section>

                <section className="py-20 md:py-28">
                    <div className="mx-auto max-w-7xl px-5 md:px-10">
                        <Reveal>
                            <div className="bg-ink text-paper p-10 md:p-16 flex flex-wrap items-center justify-between gap-6">
                                <div>
                                    <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl max-w-xl">
                                        Let's scope your build.
                                    </h2>
                                    <p className="mt-4 text-paper/60 max-w-md leading-relaxed">
                                        One free hour, in person or on Google Meet. You leave with a plan —
                                        and a fixed quote within 48 hours.
                                    </p>
                                </div>
                                <a
                                    href="/?book=web-dev#booking"
                                    data-testid="webdev-bottom-cta"
                                    className="inline-flex items-center gap-2 bg-worx text-white px-8 py-4 font-medium hover:bg-paper hover:text-ink transition-colors duration-200"
                                >
                                    Book a free dev consult <ArrowUpRight size={16} />
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
